import { useRoadsideAssistance } from './../roadside-assistance/roadside-assistance.state';
import { defineStore } from 'pinia';
import { MakeAPaymentService } from '@/services/store/make-a-payment.service';
import { computed, ref } from 'vue';
import {
  IApplyAssistanceQuoteDiscountRequest,
  IApplyAssistanceQuoteDiscountResponse,
  IPayWithCardResponse,
  IPaymentReferenceDetails,
  IProcessPaymentRequest,
  IQuotedOption,
  IReceiptEmailResponse,
  IReceiptRequest,
  ISuccessResponse,
} from '@/interfaces/make-a-payment/make-a-payment.interface';
import { ICardDetails, IOffer } from '@/interfaces';
import { mapDirectDebitRequest } from '@/forms/make-a-payment/mappings/make-a-payment-mappings';
import { CardTypes } from '@/enums/payment-types.enum';

export const useMakeAPaymentStore = defineStore('MakeAPayment', () => {
  // ------------------------------------------------------------- //
  // Foundation Store
  // ------------------------------------------------------------- //
  const rsaStore = useRoadsideAssistance();

  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const paymentReferenceDetails = ref<IPaymentReferenceDetails>({} as IPaymentReferenceDetails);
  const paymentResponse = ref<IPayWithCardResponse>();
  const selectedQuotedOption = ref<IQuotedOption>();
  const addedDirectDebit = ref<boolean>(false);
  const ddLoading = ref<boolean>(false);
  const directDebitAttempted = ref<boolean>(false);
  const directDebitSuccess = ref<boolean>(false);
  const directDebitFailed = ref<boolean>(false);
  const roadsideUpgradeFrequencyLabel = ref<string>();
  const isBusinessRoadsidePolicy = ref(false);
  const isUpgrade = computed(() =>
    selectedQuotedOption.value
      ? selectedQuotedOption.value?.productCode != paymentReferenceDetails.value.currentProduct.productCode
      : false
  );
  const isLifeStyle = computed((): boolean => paymentReferenceDetails.value?.policyTitle == 'Lifestyle');

  const isMultiCoveragePolicy = computed((): boolean => {
    return !!paymentReferenceDetails.value?.coverageCode && !!paymentReferenceDetails.value?.policyTitle;
  });

  const setSelectedQuoteOption = (quote: IQuotedOption) => {
    selectedQuotedOption.value = quote;
  };
  /**
   * Get prn details
   * @param prn payment reference number
   * @returns
   */
  const getPrnDetails = async (prn: string) => {
    const response = await MakeAPaymentService.getPrnDetails(prn);
    paymentReferenceDetails.value.prn = prn;
    response.isInsuranceClaim =
      response.paymentPillar != null && response.paymentPillar.toLowerCase() == 'insurance claim';
    response.isInurance =
      !(response.renewalOptions && response.renewalOptions?.length > 0) && (response.amount ?? 0) > 0;
    response.isInvoiceDebt = response.description == 'Invoice/Debtor payments';
    response.hasVehicleDetails =
      (response.isInurance && response.coverageCode == 'MC') ||
      (!response.isInurance && response.policyTitle != 'Lifestyle' && response.amount != null);
    response.prn = prn;
    paymentReferenceDetails.value = response;

    // prepare t&c for roadside policy
    if (!response.isInurance && !response.isInsuranceClaim && !response.isInvoiceDebt) {
      setupTermsAndConditions();
    }
  };

  /**
   * Setup t&c for roadside products
   */
  async function setupTermsAndConditions() {
    const productDetails = await rsaStore.getRsaProductContent(
      paymentReferenceDetails.value?.currentProduct?.productCode
    );
    isBusinessRoadsidePolicy.value = productDetails.isBusiness ?? false;
  }

  /**
   * Process payment for prn
   * @param cardDetails card details
   * @returns
   */
  const processPrnPayment = async (cardDetails: ICardDetails): Promise<IPayWithCardResponse> => {
    const basePaymentRequest = {
      amount: paymentReferenceDetails.value?.amount ?? 0,
      prn: paymentReferenceDetails.value?.prn,
      type: paymentReferenceDetails.value?.description,
      cartItem: {
        apttusQuoteId:
          selectedQuotedOption.value?.apttusQuoteId ?? paymentReferenceDetails.value.currentProduct.apttusQuoteId,
        cartId: selectedQuotedOption.value?.apttusCartId ?? paymentReferenceDetails.value.currentProduct.apttusCartId,
        invoiceId: '',
      },
    };

    // DS-5322 need to update policy title for later receipt email
    if (selectedQuotedOption.value?.product) {
      paymentReferenceDetails.value.policyTitle = selectedQuotedOption.value.product;
    }

    const isWalletPayment = [CardTypes.GooglePay, CardTypes.ApplePay].includes(cardDetails?.cardType as CardTypes);

    const paymentRequest: IProcessPaymentRequest = {
      paymentReferenceDetails: paymentReferenceDetails.value,
      paymentRequest: isWalletPayment
        ? {
            ...basePaymentRequest,
            walletToken: cardDetails?.token,
            walletProvider: cardDetails?.cardType,
          }
        : {
            ...basePaymentRequest,
            cardToken: cardDetails?.token,
          },
    };

    const response = await MakeAPaymentService.processPayment(paymentRequest);
    paymentResponse.value = response;
    return response;
  };

  /**
   * Send receipt email to user
   * @param receiptRequest receipt request for sending email
   * @returns
   */
  const sendReceiptEmail = async (receiptRequest: IReceiptRequest): Promise<IReceiptEmailResponse> => {
    return await MakeAPaymentService.sendReceiptEmail(receiptRequest);
  };

  /**
   * apply discount to quote
   * @param discountRequest quote discount request
   * @returns
   */
  const applyDiscountToQuote = async (
    discountRequest: IApplyAssistanceQuoteDiscountRequest
  ): Promise<IApplyAssistanceQuoteDiscountResponse> => {
    return await MakeAPaymentService.applyDiscountToQuote(discountRequest);
  };

  /**
   * Sort upgrade options based on DI preference
   * @param rsaQuoteOptions
   */
  const sortRenewalOptionsBasedOnOffer = (rsaQuoteOptions?: IOffer[]) => {
    if (rsaQuoteOptions?.length) {
      paymentReferenceDetails.value.renewalOptions.sort((a: IQuotedOption, b: IQuotedOption) => {
        if (a.productCode == paymentReferenceDetails.value.renewalOptions[0].productCode) return -1; //do not sort the current 1st product as it will need to remain at 1
        if (b.productCode == paymentReferenceDetails.value.renewalOptions[0].productCode) return 1;
        return (
          (rsaQuoteOptions.find((x) => x.data.quote.productCode == a.productCode)?.rank ?? 10) - //choosing higher number rank so that ones without rank are always sorted at the end
          (rsaQuoteOptions.find((x) => x.data.quote.productCode == b.productCode)?.rank ?? 10)
        );
      });
    }
  };

  /**
   * Add direct debit to asset line item
   * @param cardDetails card details
   * @returns
   */
  const addDirectDebitToPrn = async (cardDetails: ICardDetails): Promise<ISuccessResponse> => {
    const response = await MakeAPaymentService.addDirectDebitToPrn(
      paymentReferenceDetails.value.apttusId,
      mapDirectDebitRequest(paymentReferenceDetails.value, cardDetails)
    );
    addedDirectDebit.value = response.value;
    return response;
  };
  /**
   * set direct debit
   * @param cardDetails
   */
  const setDirectDebit = async (cardDetails: ICardDetails | null) => {
    try {
      if (cardDetails) {
        ddLoading.value = true;
        await addDirectDebitToPrn(cardDetails);
        setDDStatus(true);
      }
    } catch (e) {
      console.error('Failed to setup direct debit', e);
      setDDStatus(false);
    } finally {
      ddLoading.value = false;
    }
  };
  const setDDStatus = (success: boolean) => {
    directDebitAttempted.value = true;
    directDebitSuccess.value = success;
    directDebitFailed.value = !success;
  };

  const referrerUrl = ref<string>('');

  const setReferrerUrl = (url: string) => {
    referrerUrl.value = url;
  };

  const $reset = () => {
    selectedQuotedOption.value = undefined;
    paymentReferenceDetails.value = {} as IPaymentReferenceDetails;
  };
  return {
    paymentReferenceDetails,
    roadsideUpgradeFrequencyLabel,
    selectedQuotedOption,
    getPrnDetails,
    processPrnPayment,
    paymentResponse,
    sendReceiptEmail,
    setSelectedQuoteOption,
    applyDiscountToQuote,
    sortRenewalOptionsBasedOnOffer,
    addDirectDebitToPrn,
    addedDirectDebit,
    setDirectDebit,
    ddLoading,
    directDebitAttempted,
    directDebitSuccess,
    directDebitFailed,
    isUpgrade,
    isLifeStyle,
    isMultiCoveragePolicy,
    isBusinessRoadsidePolicy,
    referrerUrl,
    setReferrerUrl,
    $reset,
  };
});
